// Ääntämisvalmentaja's platform strings (docs/design/AANTAMISVALMENTAJA.md, area `coachLive`; work package D):
// the coach's files on the phone (Asetukset → Ääni, coach users only: coach/model/ModelStorage.tsx), the
// microphone's states and notices for the live screen (coach/live/session.ts), and the device diagnostics in
// Ylläpito → Puhelinten kunto (health/HealthAdmin.tsx). Finnish (default) and English side by side; en has
// the same shape. Style: docs/FINNISH-STYLE.md (sinä, no internal jargon, no intro text, "1 tiedosto").
import { defineArea } from '../area';

const fiNum = (n: number, digits = 0) => n.toLocaleString('fi-FI', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const enNum = (n: number, digits = 0) => n.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const mbFi = (b: number) => `${fiNum(b / 1e6, b < 10e6 ? 1 : 0)} Mt`;
const mbEn = (b: number) => `${enNum(b / 1e6, b < 10e6 ? 1 : 0)} MB`;

export const coachLiveFi = {
  files: {
    h: 'Ääntämisvalmentaja laitteelle',
    download: (bytes: number) => `Lataa valmentajan tiedostot (${mbFi(bytes)})`,
    resume: (bytes: number) => `Jatka latausta (${mbFi(bytes)})`,
    stop: 'Keskeytä',
    have: (bytes: number) => `Valmentajan tiedostot ovat laitteella (${mbFi(bytes)}).`,
    partly: (have: number, total: number) => `Laitteella ${mbFi(have)} / ${mbFi(total)}.`,
    loading: 'Ladataan valmentajan tiedostoja…',
    /** "23 / 98 Mt · 2/5 tiedostoa" */
    amount: (done: number, total: number, files: number, filesTotal: number) =>
      `${fiNum(done / 1e6)} / ${fiNum(total / 1e6)} Mt · ${files}/${filesTotal} tiedostoa`,
    amountLabel: (done: number, total: number) => `${fiNum(done / 1e6)} / ${fiNum(total / 1e6)} megatavua`,
    stoppedNetwork: 'Lataus keskeytyi. Yritä uudelleen.',
    stoppedOffline: 'Yhteys katkesi. Jatka latausta, kun yhteys palaa.',
    stoppedStorage: 'Laitteella ei ole tilaa valmentajan tiedostoille.',
    stoppedCorrupt: 'Ladattu tiedosto oli viallinen. Yritä uudelleen.',
    room: (need: number, free: number) => `Tarvitaan noin ${fiNum(need / 1e6)} Mt, vapaana ${fiNum(free / 1e6)} Mt.`,
    checking: 'Tarkistetaan, toimiiko ääntämisen tunnistus tässä puhelimessa…',
    works: 'Ääntämisen tunnistus toimii tässä puhelimessa.',
    fails: 'Ääntämisen tunnistus ei toimi tässä puhelimessa. Valmentaja käyttää vain mittareita.',
    checkAgain: 'Tarkista uudelleen',
    remove: 'Poista valmentajan tiedostot',
    notPersisted: 'Selain voi poistaa ladatut tiedostot, jos laitteen tila käy vähiin.',
    offlineNow: 'Ei yhteyttä. Lataa tiedostot, kun yhteys palaa.',
    unsupported: 'Tämä selain ei pysty tallentamaan valmentajan tiedostoja.',
    retry: 'Yritä uudelleen',
    credits: 'Ääntämisen tunnistus: ZIPA-CR-NS-small (CC BY-NC-SA 4.0). Huulikamera: MediaPipe Face Landmarker (Apache 2.0).',
  },
  mic: {
    starting: 'Avataan mikrofonia…',
    denied: 'Mikrofonin käyttö on estetty. Salli se selaimen tai iPhonen asetuksista ja yritä uudelleen.',
    unsupported: 'Tämä selain ei pysty kuuntelemaan mikrofonia.',
    busy: 'Mikrofoni on toisen sovelluksen käytössä.',
    error: 'Mikrofonia ei saatu auki. Yritä uudelleen.',
    continue: 'Jatka',
    notices: {
      silence: 'Mikrofoni suljettiin, koska ääntä ei kuulunut minuuttiin.',
      background: 'Mikrofoni suljettiin, kun sovellus siirtyi taustalle.',
      device: 'Äänilaite vaihtui.',
      system: 'Puhelin keskeytti mikrofonin.',
      ended: 'Mikrofoni sulkeutui.',
      playback: 'Mikrofoni on kiinni Petersonin äänitteen ajan.',
      busy: 'Mikrofoni on toisen sovelluksen käytössä.',
      'no-device': 'Mikrofonia ei löytynyt. Kytke mikrofoni ja yritä uudelleen.',
      stalled: 'Kuuntelu pysähtyi.',
      'after-playback': 'Petersonin äänite loppui. Jatka, niin kuuntelen taas.',
    },
    bandLimited: 'Tämä mikrofoni ei kuule korkeimpia ääniä: sihinän paikkaa ei mitata, muu toimii. Puhelimen mikrofoni kuulee kaiken.',
  },
  diag: {
    h: 'Ääntämisvalmentaja',
    none: 'Yksikään laite ei ole vielä lähettänyt valmentajan tietoja.',
    kinds: {
      builtin: 'puhelimen mikrofoni',
      airpods: 'AirPods',
      bluetooth: 'Bluetooth-mikrofoni',
      headset: 'kuulokemikrofoni',
      usb: 'USB-mikrofoni',
      other: 'muu mikrofoni',
      unknown: 'tuntematon mikrofoni',
    },
    rate: (hz: number) => `${fiNum(hz / 1000, hz % 1000 ? 1 : 0)} kHz`,
    raw: 'ilman puheenkäsittelyä',
    processed: 'puheenkäsittely päällä',
    band: (hz: number) => `kuuluu ${fiNum(hz / 1000, 1)} kHz asti`,
    bandLimited: 'ei kuule korkeita ääniä',
    hop: (ms: number) => `lohkojen väli 95 %:ssa alle ${fiNum(ms, 1)} ms`,
    noise: (db: number) => `taustamelu ${fiNum(db)} dB`,
    modelOk: (ms: number) => `tunnistus toimii (${fiNum(ms)} ms)`,
    modelFails: (why: string) => `tunnistus ei toimi (${why})`,
    lipsOk: (fps: number) => `huulikamera ${fiNum(fps, 1)} kuvaa/s`,
    lipsFails: (why: string) => `huulikamera ei toimi (${why})`,
    playback: {
      same: 'toisto kuulostaa samalta mikrofonin ollessa auki',
      'worse-with-mic': 'toisto huononee mikrofonin ollessa auki',
      unsure: 'toistosta ei varmuutta',
    },
    breaks: (n: number, routes: number) => `keskeytyksiä ${n}, äänilaitteen vaihtoja ${routes}`,
  },
};

export const coachLiveEn: typeof coachLiveFi = {
  files: {
    h: 'Pronunciation coach on this device',
    download: (bytes) => `Download the coach's files (${mbEn(bytes)})`,
    resume: (bytes) => `Continue the download (${mbEn(bytes)})`,
    stop: 'Stop',
    have: (bytes) => `The coach's files are on this device (${mbEn(bytes)}).`,
    partly: (have, total) => `${mbEn(have)} / ${mbEn(total)} on this device.`,
    loading: "Downloading the coach's files…",
    amount: (done, total, files, filesTotal) => `${enNum(done / 1e6)} / ${enNum(total / 1e6)} MB · ${files}/${filesTotal} files`,
    amountLabel: (done, total) => `${enNum(done / 1e6)} / ${enNum(total / 1e6)} megabytes`,
    stoppedNetwork: 'The download stopped. Try again.',
    stoppedOffline: 'The connection dropped. Continue when you are back online.',
    stoppedStorage: "This device has no room for the coach's files.",
    stoppedCorrupt: 'A downloaded file was damaged. Try again.',
    room: (need, free) => `About ${enNum(need / 1e6)} MB needed, ${enNum(free / 1e6)} MB free.`,
    checking: 'Checking whether pronunciation recognition works on this phone…',
    works: 'Pronunciation recognition works on this phone.',
    fails: 'Pronunciation recognition does not work on this phone. The coach uses the meters only.',
    checkAgain: 'Check again',
    remove: "Remove the coach's files",
    notPersisted: 'The browser may remove the downloaded files if the device runs low on space.',
    offlineNow: 'Offline. Download the files when you are back online.',
    unsupported: "This browser cannot store the coach's files.",
    retry: 'Try again',
    credits: 'Pronunciation recognition: ZIPA-CR-NS-small (CC BY-NC-SA 4.0). Lips camera: MediaPipe Face Landmarker (Apache 2.0).',
  },
  mic: {
    starting: 'Opening the microphone…',
    denied: 'Microphone access is blocked. Allow it in the browser or iPhone settings and try again.',
    unsupported: 'This browser cannot listen to the microphone.',
    busy: 'Another app is using the microphone.',
    error: 'The microphone did not open. Try again.',
    continue: 'Continue',
    notices: {
      silence: 'The microphone was closed: nothing was heard for a minute.',
      background: 'The microphone was closed when the app went to the background.',
      device: 'The audio device changed.',
      system: 'The phone interrupted the microphone.',
      ended: 'The microphone closed.',
      playback: "The microphone is closed while Peterson's recording plays.",
      busy: 'Another app is using the microphone.',
      'no-device': 'No microphone was found. Connect one and try again.',
      stalled: 'Listening stopped.',
      'after-playback': "Peterson's recording has ended. Continue and I'll listen again.",
    },
    bandLimited:
      "This microphone does not reach the highest sounds: the hiss's place is not measured, everything else works. The phone's microphone hears it all.",
  },
  diag: {
    h: 'Pronunciation coach',
    none: 'No device has sent coach details yet.',
    kinds: {
      builtin: "the phone's microphone",
      airpods: 'AirPods',
      bluetooth: 'a Bluetooth microphone',
      headset: 'a headset microphone',
      usb: 'a USB microphone',
      other: 'another microphone',
      unknown: 'an unknown microphone',
    },
    rate: (hz) => `${enNum(hz / 1000, hz % 1000 ? 1 : 0)} kHz`,
    raw: 'no voice processing',
    processed: 'voice processing on',
    band: (hz) => `hears up to ${enNum(hz / 1000, 1)} kHz`,
    bandLimited: 'does not hear high sounds',
    hop: (ms) => `block gap under ${enNum(ms, 1)} ms 95 % of the time`,
    noise: (db) => `background noise ${enNum(db)} dB`,
    modelOk: (ms) => `recognition works (${enNum(ms)} ms)`,
    modelFails: (why) => `recognition does not work (${why})`,
    lipsOk: (fps) => `lips camera ${enNum(fps, 1)} frames/s`,
    lipsFails: (why) => `lips camera does not work (${why})`,
    playback: {
      same: 'playback sounds the same with the microphone open',
      'worse-with-mic': 'playback gets worse with the microphone open',
      unsure: 'playback: not sure',
    },
    breaks: (n, routes) => `${n} interruptions, ${routes} audio device changes`,
  },
};

// Messages.coachLive (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('coachLive', coachLiveFi, coachLiveEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof coachLiveFi> {}
}
